import React, { useEffect, useState } from "react";
import { apiGet, apiDelete } from "../../../utils/apiFetch";
import apiPath from "../../../utils/pathObj";
import OButton from "components/reusable/OButton";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import dayjs from "dayjs";
import Table from "./Table";
import Pagination from "../../Pagination";
import { useTranslation } from "react-i18next";
import ListingView from "./ListingView";
import helpers from "utils/helpers";
import { useLocation } from "react-router-dom";
import CustomSelect from "components/reusable/CustomSelect";

const Listing = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [employeeList, setEmployeeList] = useState([]);
  const [currentItem, setCurrentItem] = useState("");
  const [employeeView, setEmployeeView] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterData, setFilterData] = useState({
    employeeType: "",
    searchKey: "",
    startDate: "",
    endDate: "",
    departmentType: "",
    isReset: false,
    isFilter: false,
  });
  const [sort, setSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
    sortColumn: "applied_date" // Add this for column selection
  });
  const [departmentList, setDepartmentList] = useState("");
  
  // Define sorting options
  const [sortOptions] = useState([
    { value: 'a_z', label: 'A-Z' },
    { value: 'z_a', label: 'Z-A' }
  ]);
  
  // Define column options for sorting
  const [columnOptions] = useState([
    { value: 'employee_name', label: t('EMPLOYEE_NAME') },
    { value: 'job_title', label: t('JOB_TITLE') },
  ]);

  const handleView = (item) => {
    setCurrentItem(item);
    setEmployeeView(!employeeView);
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  const getDepartmentList = async () => {
    try {
      const res = await apiGet(apiPath.getDepartmentList, {});
      setDepartmentList(res?.data?.results);
    } catch (error) {
      console.log("error:", error);
    }
  };

  const allEmployee = async (data) => {
    try {
      const { employeeType, startDate, endDate, searchKey, isFilter, departmentType } = filterData;
      if ((data?.deletePage && employeeList?.length >= 1) || (isFilter && data?.statusChange && employeeList?.length >= 1)) {
        setPage(page - 1);
        setPaginationObj({ ...paginationObj, page: page - 1 });
      }

      // Map UI sort column to backend sortBy field
      const getSortByField = () => {
        switch (sort.sortColumn) {
          case 'employee_name':
            return 'Employee.name'
          case 'job_title':
            return 'Job.name'
          case 'department':
            return 'JobDepartment.name'
          case 'experience':
            return 'yearOfExperiece'
          case 'applied_date':
            return 'createdAt'
          default:
            return 'createdAt'
        }
      }

      const payload = {
        page,
        pageSize,
        status: employeeType,
        startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format("YYYY-MM-DD"), null),
        endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format("YYYY-MM-DD"), null),
        keyword: searchKey,
        sortBy: getSortByField(),
        sortType: sort.sortType,
        departmentID: departmentType,
      };
      const path = apiPath.employeeList;
      const result = await apiGet(path, payload);
      const response = result?.data?.results;
      const resultStatus = result?.data?.success;
      setEmployeeList(response?.docs);
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, response.page, null),
        pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
        perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
        totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null),
      });
    } catch (error) {
      console.log("error in get all employee list==>>>>", error.message);
    }
  };

  useEffect(() => {
    allEmployee();
  }, [page, filterData, sort]);

  useEffect(() => {
    getDepartmentList();
  }, []);

  const setDepartmentType = (e) => {
    setFilterData({
      ...filterData,
      departmentType: e.target.value,
      isFilter: true,
      isReset: false,
    });
    setPage(1);
  };

  const handleReset = () => {
    setFilterData({
      employeeType: "",
      searchKey: "",
      startDate: "",
      endDate: "",
      departmentType: "",
      isReset: true,
      isFilter: false,
    });
    setSort({
      sortBy: "createdAt",
      sortType: "desc",
      sortColumn: "applied_date"
    });
    setPage(1);
    setSearchTerm("");
  };

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
  };
  
  useEffect(() => {
    if (location.state) setFilterData({ ...filterData, employeeType: location.state });
  }, [location.state]);

  const statusPage = (e) => {
    setFilterData({
      ...filterData,
      employeeType: e.target.value,
      isFilter: true,
    });
    setPage(1);
  };

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage,
    });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const handleDeleteJobRequest = async (id) => {
    try {
      const result = await apiDelete(`${apiPath.deleteJobRequest}/${id}`);
      if (result?.data?.success) {
        await allEmployee(); // Re-fetch list on successful delete
      }
    } catch (err) {
      console.error("Delete error:", err.message);
    }
  };
  
  // Handle sort order change (A-Z/Z-A)
  const handleSortOrderChange = e => {
    const orderValue = e.target.value
    setSort(prev => ({
      ...prev,
      sortType: orderValue === 'a_z' ? 'asc' : 'desc'
    }))
    setPage(1)
  }

  // Handle sort column change
  const handleSortColumnChange = e => {
    const columnValue = e.target.value
    setSort(prev => ({
      ...prev,
      sortColumn: columnValue,
      // Reset to ascending when changing column
      sortType: 'asc'
    }))
    setPage(1)
  }
  
  return (
    <>
      <div className="bg-[#F9F9F9]">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          <form className="border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3 flex-wrap justify-between">
            <div className="col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0">
              <div className="flex items-center lg:pt-0 pt-3 flex-wrap ">
                <div className="relative flex items-center mb-3 w-[320px]">
                  <div className="absolute inset-y-0 right-0 flex items-center pl-3 mr-3 pointer-events-none">
                    {!searchTerm ? (
                      <svg aria-hidden="true" className="w-4 h-4 text-[#A5A5A5] dark:text-gray-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    ) : (
                      ""
                    )}
                  </div>
                  <input
                    type="search"
                    id="default-search"
                    className="block w-full p-2 outline-none text-sm text-gray-900 2xl:min-w-[250px] xl:min-w-[300px] rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Employee name and ID, department, email"
                    value={searchTerm}
                    title=""
                    required
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <ODateRangePicker handleDateChange={handleDateChange} isReset={filterData?.isReset} setIsReset={setFilterData} />
                <div className="flex items-center ml-3 mb-3">
                  <select
                    id=""
                    type="text "
                    name="employeeType"
                    className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                    placeholder=" "
                    onChange={(e) => statusPage(e)}
                    value={filterData?.employeeType}
                  >
                    <option value="">
                      {t("O_ALL")}
                    </option>
                    <option value="accepted">{t("ACCEPTED")}</option>
                    <option value="pending">{t("PENDING")}</option>
                    <option value="rejected">{t("REJECTED")}</option>
                    <option value="terminated">{t("TERMINATED")}</option>
                  </select>
                </div>
                <div className="flex items-center mb-3 ml-3">
                  <select
                    className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                    placeholder=" "
                    type="select"
                    id="departmentID"
                    value={filterData?.departmentType}
                    onChange={(e) => setDepartmentType(e)}
                  >
                    <option value="">{t("SELECT_DEPARTMENT_NAME")}</option>
                    {departmentList &&
                      departmentList.map((item) => {
                        return <option key={item?._id} value={item?._id}>{item?.name}</option>;
                      })}
                  </select>
                </div>

                {/* Sort Column Dropdown */}
                <CustomSelect
                  id='sortColumn'
                  name='sortColumn'
                  options={columnOptions}
                  placeholder={t('SORT_BY_COLUMN')}
                  value={sort.sortColumn}
                  onChange={handleSortColumnChange}
                  t={t}
                  extraClasses='ml-3 mb-3'
                />

                {/* Sort Order Dropdown */}
                <CustomSelect
                  id='sortOrder'
                  name='sortOrder'
                  options={sortOptions}
                  placeholder={t('SORT_ORDER')}
                  value={sort.sortType === 'asc' ? 'a_z' : 'z_a'}
                  onChange={handleSortOrderChange}
                  t={t}
                  extraClasses='ml-3 mb-3'
                />

                <OButton label="Reset" type="button" onClick={() => handleReset()} extraClasses="ml-3 mb-3" style={{ borderRadius: "0.5rem" }} />
              </div>
            </div>
            <div className="lg:pt-0 pt-2">
              <label htmlFor="default-search" className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">
                {t("O_SEARCH")}
              </label>
            </div>
          </form>
          <Table 
            employeeList={employeeList} 
            page={page} 
            allEmployee={allEmployee} 
            handleView={handleView} 
            currentItem={currentItem} 
            employeeView={employeeView} 
            setSort={setSort} 
            sort={sort} 
            setPage={setPage} 
            handleDeleteJobRequest={handleDeleteJobRequest} 
          />
          {paginationObj?.totalItems ? <Pagination handlePageClick={handlePageClick} options={paginationObj} page={page} /> : null}
        </div>
        {employeeView && <ListingView setEmployeeView={setEmployeeView} allEmployee={allEmployee} currentItem={currentItem} />}
      </div>
    </>
  );
};

export default Listing;